/**
 * Detecta a codificação de um arquivo baseado em padrões de bytes
 * @param {ArrayBuffer} buffer - Buffer do arquivo
 * @returns {string} - Codificação detectada
 */
export function detectEncoding(buffer) {
  const uint8Array = new Uint8Array(buffer);
  
  // Verifica BOM UTF-8
  if (uint8Array.length >= 3 && 
      uint8Array[0] === 0xEF && 
      uint8Array[1] === 0xBB && 
      uint8Array[2] === 0xBF) {
    return 'UTF-8';
  }
  
  // Verifica BOM UTF-16 LE
  if (uint8Array.length >= 2 && 
      uint8Array[0] === 0xFF && 
      uint8Array[1] === 0xFE) {
    return 'UTF-16LE';
  }
  
  // Verifica BOM UTF-16 BE
  if (uint8Array.length >= 2 && 
      uint8Array[0] === 0xFE && 
      uint8Array[1] === 0xFF) {
    return 'UTF-16BE';
  }
  
  let hasHighBits = false;
  let validUtf8Sequences = 0;
  let invalidUtf8Sequences = 0;
  let possibleWindows1252 = 0;
  
  // Bytes específicos do Windows-1252 que são comuns em português
  const windows1252PortugueseBytes = [0xE7, 0xE3, 0xF5, 0xE1, 0xE9, 0xED, 0xF3, 0xFA, 0xE0, 0xEA, 0xF4]; // ç, ã, õ, á, é, í, ó, ú, à, ê, ô
  
  for (let i = 0; i < Math.min(uint8Array.length, 8192); i++) {
    const byte = uint8Array[i];
    
    if (byte > 127) {
      hasHighBits = true;
      
      // Verifica se é um byte comum do Windows-1252 para português
      if (windows1252PortugueseBytes.includes(byte)) {
        possibleWindows1252++;
      }
      
      // Verifica sequências UTF-8 válidas
      if ((byte & 0xE0) === 0xC0) { // 110xxxxx - 2 bytes
        if (i + 1 < uint8Array.length && (uint8Array[i + 1] & 0xC0) === 0x80) {
          validUtf8Sequences++;
          i++; // Pula o próximo byte
        } else {
          invalidUtf8Sequences++;
        }
      } else if ((byte & 0xF0) === 0xE0) { // 1110xxxx - 3 bytes
        if (i + 2 < uint8Array.length && 
            (uint8Array[i + 1] & 0xC0) === 0x80 && 
            (uint8Array[i + 2] & 0xC0) === 0x80) {
          validUtf8Sequences++;
          i += 2; // Pula os próximos 2 bytes
        } else {
          invalidUtf8Sequences++;
        }
      } else if ((byte & 0xF8) === 0xF0) { // 11110xxx - 4 bytes
        if (i + 3 < uint8Array.length && 
            (uint8Array[i + 1] & 0xC0) === 0x80 && 
            (uint8Array[i + 2] & 0xC0) === 0x80 && 
            (uint8Array[i + 3] & 0xC0) === 0x80) {
          validUtf8Sequences++;
          i += 3; // Pula os próximos 3 bytes
        } else {
          invalidUtf8Sequences++;
        }
      }
    }
  }
  
  if (!hasHighBits) {
    return 'UTF-8';
  }
  
  if (validUtf8Sequences > 0 && invalidUtf8Sequences === 0) {
    return 'UTF-8';
  }
  
  if (possibleWindows1252 > 0) {
    return 'windows-1252';
  }
  
  if (invalidUtf8Sequences > validUtf8Sequences) {
    return 'windows-1252';
  }
  
  return 'UTF-8';
}

/**
 * Converte um arquivo para UTF-8
 * @param {File} file - Arquivo original
 * @returns {Promise<File>} - Arquivo convertido para UTF-8
 */
export async function convertFileToUtf8(file) {
  try {
    const buffer = await file.arrayBuffer();
    const detectedEncoding = detectEncoding(buffer);
    const bestResult = await findBestEncoding(buffer);
    
    let finalText = bestResult.text;
    let finalEncoding = bestResult.encoding;
    
    if (detectedEncoding !== 'UTF-8') {
      const encodingsToTest = ['windows-1252', 'iso-8859-1'];
      let bestEncoding = finalEncoding;
      let bestText = finalText;
      let lowestProblemChars = (finalText.match(/[�\uFFFD]/g) || []).length;
      
      for (const encoding of encodingsToTest) {
        try {
          const decoder = new TextDecoder(encoding);
          const testText = decoder.decode(buffer);
          const problemChars = (testText.match(/[�\uFFFD]/g) || []).length;
        
          
          if (problemChars < lowestProblemChars) {
            bestText = testText;
            bestEncoding = encoding;
            lowestProblemChars = problemChars;
          }
        } catch (error) {
          console.warn(`Erro ao testar ${encoding}:`, error);
        }
      }
      
      finalText = bestText;
      finalEncoding = bestEncoding;
    }
    
    if (finalEncoding === 'UTF-8' && !hasEncodingIssues(finalText)) {
      return file;
    }
    
    const utf8Bom = '\uFEFF'; // Byte Order Mark UTF-8
    const contentWithBom = utf8Bom + finalText;
    
    const utf8Blob = new Blob([contentWithBom], { type: file.type || 'text/csv;charset=utf-8' });
    const utf8File = new File([utf8Blob], file.name, {
      type: file.type || 'text/csv;charset=utf-8',
      lastModified: file.lastModified
    });
    
    return utf8File;
    
  } catch (error) {
    console.warn('Erro ao converter codificação, usando arquivo original:', error);
    return file;
  }
}

/**
 * Verifica se um texto contém caracteres que podem indicar problemas de codificação
 * @param {string} text - Texto para verificar
 * @returns {boolean} - True se há possíveis problemas de codificação
 */
export function hasEncodingIssues(text) {
  const problematicChars = /[�\uFFFD]|[\x80-\x9F]/g;
  return problematicChars.test(text);
}

/**
 * Tenta múltiplas codificações para encontrar a melhor
 * @param {ArrayBuffer} buffer - Buffer do arquivo
 * @returns {Promise<{encoding: string, text: string}>} - Melhor codificação e texto
 */
export async function findBestEncoding(buffer) {
  const encodings = ['UTF-8', 'windows-1252', 'ISO-8859-1', 'UTF-16LE', 'UTF-16BE'];
  let bestResult = null;
  let lowestErrorScore = Infinity;
  
  for (const encoding of encodings) {
    try {
      const decoder = new TextDecoder(encoding, { fatal: false });
      const text = decoder.decode(buffer);
      
      const errorScore = (text.match(/[�\uFFFD]/g) || []).length;
      
      if (errorScore < lowestErrorScore) {
        lowestErrorScore = errorScore;
        bestResult = { encoding, text };
      }
      
      if (errorScore === 0) {
        break;
      }
    } catch (error) {
      console.warn(`Falha ao decodificar com ${encoding}:`, error);
    }
  }
  
  return bestResult || { encoding: 'UTF-8', text: new TextDecoder('UTF-8').decode(buffer) };
}